import { useState } from 'react';
import { Share2, Calendar, Clock, Ticket as TicketIcon } from 'lucide-react';
import { LoginRequiredCard } from './LoginRequiredCard';
import { EmptyStateCard } from './EmptyStateCard';
import { AccountLayout } from './shared/AccountLayout';
import { PaymentMethodChip } from './tickets/PaymentMethodChip';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import { useTicketStore } from '../store/ticketStore';

export interface TicketData {
  id: string;
  origin: string;
  destination: string;
  date: string;
  time: string;
  status: 'confirmed' | 'pending' | 'expiring-soon' | 'expired' | 'cancelled';
  seat: string;
  total: number;
  company: string;
  bookingId: string;
  unit: string;
  arrivalTime: string;
  passengerName: string;
  paymentMethod?: 'card' | 'link' | 'spei' | 'cash';
  paymentDeadline?: string;
  paymentStatus?: 'confirmed' | 'pending';
}

interface StatusBadgeProps {
  status: 'confirmed' | 'pending' | 'expiring-soon' | 'expired' | 'cancelled';
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const config = {
    confirmed: {
      bg: 'bg-[#ECFDF5]',
      text: 'text-[#10B981]',
      label: 'Confirmado'
    },
    pending: {
      bg: 'bg-[#FEF9C3]',
      text: 'text-[#CA8A04]',
      label: 'Pendiente de pago'
    },
    'expiring-soon': {
      bg: 'bg-[#FFF5F0]',
      text: 'text-[#FF6A2A]',
      label: 'Expira pronto'
    },
    expired: {
      bg: 'bg-[#F3F4F6]',
      text: 'text-[#6B7280]',
      label: 'Expirado'
    },
    cancelled: {
      bg: 'bg-[#FEE2E2]',
      text: 'text-[#DC2626]',
      label: 'Cancelado'
    }
  };

  const { bg, text, label } = config[status];

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-[8px] text-xs font-semibold ${bg} ${text}`}>
      {label}
    </span>
  );
};

interface TicketCardProps {
  ticket: TicketData;
  onClick?: () => void;
  isNext?: boolean;
  isHistory?: boolean;
}

const TicketCard = ({ ticket, onClick, isNext, isHistory }: TicketCardProps) => {
  // 🛡️ Defensive Guards
  const safeTicket = ticket ?? {
    id: '',
    origin: 'Origen',
    destination: 'Destino',
    date: '',
    time: '',
    status: 'pending' as const,
    seat: '',
    total: 0,
    company: '',
    bookingId: '',
    unit: '',
    arrivalTime: '',
    passengerName: '',
  };

  return (
    <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm overflow-hidden">
      <div className="p-4 pb-3 border-b border-[#E5E7EB] bg-[#F7F8FA]">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={safeTicket.status} />
            {isNext && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-[8px] text-xs font-semibold bg-[#FFF5F0] text-[#FF6A2A]">
                Próximo
              </span>
            )}
          </div>
          <span className="text-xs text-[#6B7280]">#{safeTicket.bookingId}</span>
        </div>
        {safeTicket.paymentMethod && (
          <div className="mt-2">
            <PaymentMethodChip 
              method={safeTicket.paymentMethod} 
              cardLast4={safeTicket.paymentMethod === 'card' ? '1234' : undefined}
              variant="compact"
            />
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-[#111827] text-lg flex-1">
            {safeTicket.origin} → {safeTicket.destination}
          </h3>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-[#6B7280]">
            <Calendar className="w-4 h-4" />
            <span>{safeTicket.date}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[#6B7280]">
            <Clock className="w-4 h-4" />
            <span>{safeTicket.time}</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-1.5 text-sm text-[#6B7280]">
            <TicketIcon className="w-4 h-4" />
            <span>Asiento: <span className="font-semibold text-[#111827]">{safeTicket.seat}</span></span>
          </div>
          <span className="font-bold text-[#111827]">${safeTicket.total} MXN</span>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={onClick}
            className={`flex-1 py-2.5 rounded-[12px] font-semibold transition-colors ${
              isHistory
                ? 'bg-[#F7F8FA] text-[#111827] hover:bg-[#E5E7EB]'
                : 'bg-[#FF6A2A] text-white hover:bg-[#C2410C]'
            }`}
          >
            {isHistory ? 'Ver recibo' : 'Ver boleto'}
          </button>
          <button className="p-2.5 border border-[#E5E7EB] rounded-[12px] text-[#6B7280] hover:bg-[#F7F8FA] transition-colors">
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const MyTicketsScreen = () => {
  // Use hooks for authentication and navigation
  const { isLoggedIn } = useAuth();
  const { goBack, goToLogin, goToProfile, goToSettings, goToHome, goToAccount, goToSearch, goToTicketDetail } = useNavigation();
  const myTicketsInitialTab = useTicketStore((state) => state.myTicketsInitialTab);
  
  const [activeTab, setActiveTab] = useState<'active' | 'history'>(myTicketsInitialTab || 'active');

  // Mock data
  const activeTickets: TicketData[] = [
    {
      id: '1',
      origin: 'CDMX',
      destination: 'Guadalajara',
      date: 'Mié 14 Ene',
      time: '08:30',
      status: 'confirmed',
      seat: '2A',
      total: 450,
      company: 'Estrella Blanca',
      bookingId: 'MB2401140001',
      unit: 'Bus 245',
      arrivalTime: '15:00',
      passengerName: 'Ana García',
      paymentMethod: 'card',
      paymentStatus: 'confirmed'
    },
    {
      id: '2',
      origin: 'CDMX',
      destination: 'Querétaro',
      date: 'Vie 16 Ene',
      time: '14:00',
      status: 'pending',
      seat: '5B',
      total: 280,
      company: 'Primera Plus',
      bookingId: 'MB2401160002',
      unit: 'Bus 182',
      arrivalTime: '17:30',
      passengerName: 'Ana García',
      paymentMethod: 'spei',
      paymentDeadline: 'Vie 16 Ene, 11:00 (hora CDMX)',
      paymentStatus: 'pending'
    },
    {
      id: '5',
      origin: 'CDMX',
      destination: 'Monterrey',
      date: 'Sáb 17 Ene',
      time: '09:00',
      status: 'expiring-soon',
      seat: '7C',
      total: 680,
      company: 'ETN',
      bookingId: 'MB2401170005',
      unit: 'Bus 421',
      arrivalTime: '21:00',
      passengerName: 'Ana García',
      paymentMethod: 'link',
      paymentDeadline: 'Sáb 17 Ene, 06:00 (hora CDMX)',
      paymentStatus: 'pending'
    }
  ];

  const historyTickets: TicketData[] = [
    {
      id: '3',
      origin: 'CDMX',
      destination: 'Puebla',
      date: 'Lun 5 Ene',
      time: '10:00',
      status: 'confirmed',
      seat: '3A',
      total: 220,
      company: 'ETN',
      bookingId: 'MB2401050003',
      unit: 'Bus 312',
      arrivalTime: '12:30',
      passengerName: 'Ana García',
      paymentMethod: 'card',
      paymentStatus: 'confirmed'
    },
    {
      id: '4',
      origin: 'Guadalajara',
      destination: 'CDMX',
      date: 'Sáb 20 Dic',
      time: '16:00',
      status: 'cancelled',
      seat: '8B',
      total: 450,
      company: 'Estrella Blanca',
      bookingId: 'MB2312200004',
      unit: 'Bus 198',
      arrivalTime: '22:30',
      passengerName: 'Ana García',
      paymentMethod: 'card',
      paymentStatus: 'confirmed'
    },
    {
      id: '6',
      origin: 'CDMX',
      destination: 'Toluca',
      date: 'Mié 8 Ene',
      time: '18:00',
      status: 'expired',
      seat: '4D',
      total: 150,
      company: 'Primera Plus',
      bookingId: 'MB2401080006',
      unit: 'Bus 156',
      arrivalTime: '19:30',
      passengerName: 'Ana García',
      paymentMethod: 'spei',
      paymentDeadline: 'Mié 8 Ene, 15:00 (hora CDMX)',
      paymentStatus: 'pending'
    }
  ];

  const displayTickets = activeTab === 'active' ? activeTickets : historyTickets;

  return (
    <AccountLayout
      title="Mis boletos"
      subtitle="Consulta tus viajes activos y pasados"
      activePage="booking"
      onBack={goBack}
      onHomeClick={goToHome}
      // onMyTripsClick={() => goToMyTickets('active')}
      onAccountClick={goToAccount}
      onProfileClick={goToProfile}
      onMyTicketsClick={() => {}}
      onSettingsClick={goToSettings}
      onLogoutClick={goBack}
      isLoggedIn={isLoggedIn}
      onLoginClick={goToLogin}
      showMobileHeader={false}
    >
      {/* Mobile Header with Tabs */}
      <div className="lg:hidden bg-white border-b border-[#E5E7EB] sticky top-0 z-10 -mt-4 -mx-4 mb-4">
        <div className="p-4 pb-3">
          <h1 className="text-lg font-bold text-[#111827] mb-3">Mis boletos</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 py-2.5 px-4 rounded-[10px] text-sm font-semibold transition-all ${
                activeTab === 'active'
                  ? 'bg-[#FF6A2A] text-white shadow-sm'
                  : 'bg-[#F7F8FA] text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              Activos
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2.5 px-4 rounded-[10px] text-sm font-semibold transition-all ${
                activeTab === 'history'
                  ? 'bg-[#FF6A2A] text-white shadow-sm'
                  : 'bg-[#F7F8FA] text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              Historial
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Tabs */}
      <div className="hidden lg:flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('active')}
          className={`py-2.5 px-6 rounded-[10px] text-sm font-semibold transition-all ${
            activeTab === 'active'
              ? 'bg-[#FF6A2A] text-white shadow-sm'
              : 'bg-[#F7F8FA] text-[#6B7280] hover:text-[#111827]'
          }`}
        >
          Activos
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`py-2.5 px-6 rounded-[10px] text-sm font-semibold transition-all ${
            activeTab === 'history'
              ? 'bg-[#FF6A2A] text-white shadow-sm'
              : 'bg-[#F7F8FA] text-[#6B7280] hover:text-[#111827]'
          }`}
        >
          Historial
        </button>
      </div>

      {/* Content */}
      <div className="space-y-3">
        {!isLoggedIn ? (
          <LoginRequiredCard onLoginClick={goToLogin} />
        ) : displayTickets.length === 0 && activeTab === 'active' ? (
          <EmptyStateCard 
            icon={<TicketIcon className="w-8 h-8 text-[#6B7280]" />}
            title="Aún no tienes boletos"
            description="Busca y reserva tu próximo viaje en autobús"
            buttonText="Buscar salidas"
            onButtonClick={goToSearch}
          />
        ) : displayTickets.length === 0 && activeTab === 'history' ? (
          <EmptyStateCard 
            icon={<TicketIcon className="w-8 h-8 text-[#6B7280]" />}
            title="Sin historial de viajes"
            description="Tus viajes completados aparecerán aquí"
            buttonText="Buscar salidas"
            onButtonClick={goToSearch}
          />
        ) : (
          displayTickets.map((ticket, index) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onClick={() => goToTicketDetail(ticket.id)}
              isNext={activeTab === 'active' && index === 0}
              isHistory={activeTab === 'history'}
            />
          ))
        )}
      </div>
    </AccountLayout>
  );
};

export default MyTicketsScreen;